import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface ApiResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export interface ContactFormData {
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
}

export interface BookingFormData {
  service: string;
  fullName: string;
  email: string;
  phone: string;
  message?: string;
}

export interface SpecializedServiceLeadFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  budget?: string;
  message: string;
  industry: string;
}

@Injectable({
  providedIn: 'root'
})
export class FormService {
  private apiUrl = environment.apiUrl || '/api';

  constructor(private http: HttpClient) {}

  /**
   * Submit contact form
   */
  submitContactForm(data: ContactFormData): Observable<ApiResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<ApiResponse>(
      `${this.apiUrl}/controllers/contact-form.php`,
      data,
      { headers }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Submit booking form
   */
  submitBookingForm(data: BookingFormData): Observable<ApiResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<ApiResponse>(
      `${this.apiUrl}/controllers/booking-form.php`,
      data,
      { headers }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Submit specialized service lead form
   */
  submitSpecializedServiceLead(data: SpecializedServiceLeadFormData): Observable<ApiResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<ApiResponse>(
      `${this.apiUrl}/controllers/specialized-service-lead.php`,
      data,
      { headers }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: any) {
    let errorMessage = 'An error occurred. Please try again later.';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = error.error.message;
    } else {
      // Server-side error
      if (error.status === 429) {
        errorMessage = 'Too many requests. Please try again later.';
      } else if (error.status === 400) {
        errorMessage = error.error?.error || 'Invalid form data. Please check your inputs.';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later or contact support.';
      } else if (error.error?.error) {
        errorMessage = error.error.error;
      }
    }

    return throwError(() => new Error(errorMessage));
  }
}
